import React, { useState, useCallback } from "react"
import {
  BooleanField,
  BooleanInput,
  Create,
  CreateButton,
  Datagrid,
  DateField,
  Edit,
  EditButton,
  ExportButton,
  FieldProps,
  FileField,
  FileInput,
  List,
  NumberField,
  NumberInput,
  ReferenceField,
  required,
  SelectInput,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  TopToolbar,
  UrlField,
  useListContext
} from "react-admin"
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { CreateActionsToolbar, EditActionsToolbar, ShowActionsToolbar } from "../common"
import { User } from "../users/types"
import { available_device_versions } from "../core/constants"

export interface Firmware {
  id: string
  filename: string
  file_url: string
  file_size: string
  checksum: string
  description: string
  version: number
  device_version: number
  enabled: boolean
  type: string
  created?: Date
  created_by?: User
  modified?: Date
  modified_by?: User
}

interface FirmwareProps extends FieldProps {
  source?: keyof Firmware
  record?: Firmware
}

const FirmwareTitle: React.FC<FirmwareProps> = ({ record = {} }) => (
  <span>Firmware {record ? `"${record.description}"` : ""}</span>
)

const useListActionsStyles = makeStyles((theme) => ({
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginRight: theme.spacing(3)
  },
  filterLabel: {
    fontWeight: 500,
    fontSize: "0.8125rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
    color: theme.palette.primary.main,
    whiteSpace: "nowrap" as const,
    lineHeight: 1
  },
  radioGroup: {
    flexDirection: "row" as const,
    flexWrap: "nowrap" as const,
    gap: theme.spacing(1)
  },
  radioLabel: {
    marginLeft: 0,
    marginRight: 0,
    "& .MuiFormControlLabel-label": {
      fontSize: "0.8125rem",
      fontWeight: 500,
      letterSpacing: "0.04em",
      textTransform: "uppercase" as const,
      color: theme.palette.primary.main
    }
  },
  radio: {
    padding: theme.spacing(0.5),
    color: theme.palette.primary.main,
    "&.Mui-checked": {
      color: theme.palette.primary.main
    }
  }
}))

const FirmwareListActions: React.FC = (props) => {
  const classes = useListActionsStyles()
  const { filterValues, setFilters, displayedFilters } = useListContext()
  const currentValue = filterValues?.device_version ?? "all"

  const handleFilterChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const val = event.target.value
      if (val === "all") {
        setFilters({ ...filterValues, device_version: "all" }, displayedFilters)
      } else {
        setFilters(
          { ...filterValues, device_version: parseInt(val, 10) },
          displayedFilters
        )
      }
    },
    [filterValues, setFilters, displayedFilters]
  )

  return (
    <TopToolbar className={classes.toolbar} {...props}>
      <div className={classes.filterGroup}>
        <Typography className={classes.filterLabel}>Filter by Version:</Typography>
        <RadioGroup
          className={classes.radioGroup}
          value={String(currentValue)}
          onChange={handleFilterChange}
        >
          <FormControlLabel
            className={classes.radioLabel}
            value="all"
            control={<Radio size="small" className={classes.radio} color="primary" />}
            label="ALL"
          />
          <FormControlLabel
            className={classes.radioLabel}
            value="1"
            control={<Radio size="small" className={classes.radio} color="primary" />}
            label="MAC 1"
          />
          <FormControlLabel
            className={classes.radioLabel}
            value="2"
            control={<Radio size="small" className={classes.radio} color="primary" />}
            label="MAC 2"
          />
          <FormControlLabel
            className={classes.radioLabel}
            value="3"
            control={<Radio size="small" className={classes.radio} color="primary" />}
            label="MAC 3"
          />
        </RadioGroup>
      </div>
      <CreateButton />
      <ExportButton />
    </TopToolbar>
  )
}

const FirmwareList: React.FC = (props) => (
  <List
    bulkActionButtons={false}
    {...props}
    sort={{ field: "version", order: "desc" }}
    actions={<FirmwareListActions />}
    filterDefaultValues={{ device_version: "all" }}
  >
    <Datagrid rowClick="show">
      <TextField source="version" />
      <TextField source="device_version" />
      <TextField source="type" />
      <BooleanField source="enabled" />
      <DateField label="Date created" source="created" showTime />
      <EditButton />
    </Datagrid>
  </List>
)

const FirmwareShow: React.FC = (props) => (
  <Show actions={<ShowActionsToolbar />} title={<FirmwareTitle />} {...props}>
    <SimpleShowLayout>
      <TextField label="Id" source="id" />
      <TextField source="version" />
      <TextField source="filename" />
      <TextField source="description" fullWidth />
      <UrlField source="file_url" />
      <NumberField source="file_size" />
      <TextField source="checksum" />
      <TextField source="device_version" />
      <TextField source="type" />
      <BooleanField source="enabled" />
      <ReferenceField source="created_by" reference="users">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField source="modified_by" reference="users">
        <TextField source="name" />
      </ReferenceField>
      <DateField label="Date created" source="created" showTime />
      <DateField label="Date modified" source="modified" showTime />
    </SimpleShowLayout>
  </Show>
)

const FirmwareCreate: React.FC = (props) => (
  <Create actions={<CreateActionsToolbar />} {...props}>
    <SimpleForm redirect="show">
      <NumberInput source="version" validate={required()} />
      <SelectInput
        source="device_version"
        choices={available_device_versions}
        defaultValue={1}
        validate={required()}
      />
      <SelectInput
        source="type"
        choices={[
          { id: "hex", name: "Hex" },
          { id: "bin", name: "Bin" },
          { id: "zip", name: "Zip" }
        ]}
        validate={required()}
      />
      <TextInput source="description" validate={required()} fullWidth />
      <FileInput
        source="firmwareFile"
        label="Upload firmware file in BIN, HEX or ZIP format"
        isRequired
      >
        <FileField source="src" title="title" />
      </FileInput>
      <BooleanInput source="enabled" defaultValue={true} />
    </SimpleForm>
  </Create>
)

const FirmwareEdit: React.FC = (props) => (
  <Edit
    actions={<EditActionsToolbar />}
    title={<FirmwareTitle />}
    mutationMode="pessimistic"
    {...props}
  >
    <SimpleForm redirect="show">
      <TextInput source="id" disabled />
      <NumberInput source="version" validate={required()} />
      <SelectInput
        source="device_version"
        choices={available_device_versions}
        defaultValue={1}
        validate={required()}
      />
      <SelectInput
        source="type"
        choices={[
          { id: "hex", name: "Hex" },
          { id: "bin", name: "Bin" },
          { id: "zio", name: "Zip" }
        ]}
        validate={required()}
        disabled
      />
      <TextInput source="description" validate={required()} fullWidth />
      <UrlField source="file_url" label="Current file url" />
      <FileInput
        source="firmwareFile"
        label="Upload firmware file in BIN, HEX or ZIP format"
        isRequired
      >
        <FileField source="src" title="title" />
      </FileInput>
      <BooleanInput source="enabled" defaultValue={true} />
    </SimpleForm>
  </Edit>
)

export default {
  list: FirmwareList,
  show: FirmwareShow,
  create: FirmwareCreate,
  edit: FirmwareEdit
}
